import { useEffect, useRef } from 'react';

// Going back the way phones do. Every open screen (the phone, a chat, the house, the profile…) is a
// layer with an entry in the browser history, so the phone's back gesture or button closes the top
// layer instead of leaving the game. Closing a screen with its own button takes its entry back out.
// The installed app (Home Screen) has no browser back, so a swipe in from the left edge does the same.

type Layer = { close: () => void };
const KEY = 'llLayer';
const layers: Layer[] = [];
// how many of our entries are in the history right now
let depth = 0;
// a history move we made ourselves, still to arrive
let moving = false;
let timer = 0;

const depthOf = (state: unknown) => {
  const value = state && typeof state === 'object' ? (state as Record<string, unknown>)[KEY] : undefined;
  return typeof value === 'number' ? value : 0;
};

// bring the history in line with the open layers, once per burst of changes (a screen closing as the
// next one opens is no change at all)
function sync() {
  timer = 0;
  if (moving) return;
  if (layers.length > depth) {
    while (depth < layers.length) {
      depth += 1;
      window.history.pushState({ ...(window.history.state as object | null), [KEY]: depth }, '');
    }
  } else if (layers.length < depth) {
    moving = true;
    window.history.go(layers.length - depth);
  }
}
const schedule = () => {
  if (!timer) timer = window.setTimeout(sync, 0);
};

function onPop(event: PopStateEvent) {
  depth = depthOf(event.state);
  if (moving) {
    moving = false;
    return schedule();
  }
  // back from the phone: close every layer above where the history now is, the top one first
  for (const layer of layers.splice(depth).reverse()) layer.close();
  schedule();
}

// a swipe in from the left edge, for the installed app
const EDGE = 24;
let swipe: { x: number; y: number } | null = null;
let hint: HTMLDivElement | null = null;
const installed = () =>
  window.matchMedia?.('(display-mode: standalone)').matches ||
  (window.navigator as Navigator & { standalone?: boolean }).standalone === true;
function showHint(dx: number) {
  if (!hint) {
    hint = document.createElement('div');
    hint.className = 'back-swipe';
    hint.setAttribute('aria-hidden', 'true');
    hint.textContent = '‹';
    document.body.append(hint);
  }
  const pull = Math.max(0, Math.min(dx, 120));
  hint.style.opacity = String(pull / 120);
  hint.style.transform = `translate(${pull / 2 - 40}px, -50%)`;
}
function onTouchStart(event: TouchEvent) {
  const touch = event.touches[0];
  swipe =
    layers.length && event.touches.length === 1 && touch && touch.clientX <= EDGE && installed()
      ? { x: touch.clientX, y: touch.clientY }
      : null;
}
function onTouchMove(event: TouchEvent) {
  const touch = event.touches[0];
  if (swipe && touch) showHint(touch.clientX - swipe.x);
}
function onTouchEnd(event: TouchEvent) {
  const touch = event.changedTouches[0];
  if (swipe && touch) {
    const dx = touch.clientX - swipe.x;
    const dy = Math.abs(touch.clientY - swipe.y);
    if (dx > 70 && dy < dx) window.history.back();
  }
  swipe = null;
  if (hint) showHint(0);
}

if (typeof window !== 'undefined') {
  // a reload on one of our entries starts again from the map
  if (depthOf(window.history.state)) window.history.replaceState({ ...(window.history.state as object), [KEY]: 0 }, '');
  window.addEventListener('popstate', onPop);
  window.addEventListener('touchstart', onTouchStart, { passive: true });
  window.addEventListener('touchmove', onTouchMove, { passive: true });
  window.addEventListener('touchend', onTouchEnd, { passive: true });
  window.addEventListener('touchcancel', onTouchEnd, { passive: true });
}

// Escape closes the top thing over a screen (a menu, a sheet, a full view) and nothing behind it: the newest one
// open hears the key first and keeps it from every screen's own Escape
const escapes: (() => void)[] = [];
function onEscape(event: KeyboardEvent) {
  const top = escapes.at(-1);
  if (event.key !== 'Escape' || !top) return;
  event.stopImmediatePropagation();
  top();
}
if (typeof window !== 'undefined') window.addEventListener('keydown', onEscape, true);

// while `open`, Escape calls `close` (and only that)
export function useEscapeLayer(open: boolean, close: () => void) {
  const latest = useRef(close);
  useEffect(() => {
    latest.current = close;
  });
  useEffect(() => {
    if (!open) return;
    const entry = () => latest.current();
    escapes.push(entry);
    return () => {
      const at = escapes.indexOf(entry);
      if (at !== -1) escapes.splice(at, 1);
    };
  }, [open]);
}

// a sheet or full view over a screen: going back or Escape closes it, and only it
export function useOverlay(open: boolean, close: () => void) {
  useBackLayer(open, close);
  useEscapeLayer(open, close);
}

// while `open`, the screen is a layer: going back calls `close`
export function useBackLayer(open: boolean, close: () => void) {
  const latest = useRef(close);
  useEffect(() => {
    latest.current = close;
  });
  useEffect(() => {
    if (!open) return;
    const layer: Layer = { close: () => latest.current() };
    layers.push(layer);
    schedule();
    return () => {
      const at = layers.indexOf(layer);
      if (at === -1) return;
      layers.splice(at, 1);
      schedule();
    };
  }, [open]);
}
